import { UploadOutlined } from '@ant-design/icons';
import { Space, message, Upload, type UploadProps } from 'antd';
import type { RcFile, UploadFile } from 'antd/lib/upload';
import { useField } from 'formik';
import { isObject, isNil, isEmpty, compact } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type UploadFileWithPreviewFormDataFragment } from '../../api/fragments/UploadFileWithPreviewFormData';
import { validateExtensions } from '../../utilities/extensions';
import readAsDataUrlAsync from '../../utilities/readAsDataUrlAsync';
import DragableItem from '../DraggableItem';
import DropWrapper from '../DropWrapper';
import FormItem, { type FormItemProps } from './FormItem';

const StyledUpload = styled(Upload)`
    & .ant-upload-list-picture-card .ant-upload-list-item-thumbnail img {
        object-fit: cover;
    }
`;

const itemRender = (originNode, file, moveItem, setDraggedElement) => (
    <DragableItem<MultiPictureType>
        item={file as MultiPictureType}
        moveItem={moveItem}
        originNode={originNode}
        setDragElement={setDraggedElement}
    />
);

export type MultiPictureWallUploadFieldProps = Omit<UploadProps, 'listType' | 'maxCount' | 'accept' | 'fileList'> & {
    name: string;
    extensions: string[];
    label?: string;
    required?: boolean;
    sizeLimitInMiB?: number;
    tooltip?: FormItemProps['tooltip'];
    style?: FormItemProps['style'];
    max?: number;
};

type MultiPictureType = UploadFile & { id: string };

const SortableUploadField = ({
    name,
    label,
    extensions,
    required = false,
    sizeLimitInMiB = 2,
    tooltip,
    style,
    max,
    disabled = false,
}: MultiPictureWallUploadFieldProps) => {
    const { t } = useTranslation(['common']);

    const accept = useMemo(() => extensions.join(','), [extensions]);

    const [field, meta, helpers] = useField<MultiPictureType[]>(name);

    const defaultFileList = useMemo(() => {
        // Using meta.value for handling unmounted effect
        if (isNil(field.value) || isEmpty(field.value)) {
            return [];
        }

        return field.value.map(file => {
            if (!(file instanceof File) && isObject(file)) {
                // Cast as api fragment
                const value = file as unknown as UploadFileWithPreviewFormDataFragment;

                return {
                    ...file,
                    uid: value.id,
                    name: value.filename,
                    thumbUrl: value.preview?.url || value.url,
                };
            }

            return file;
        });
    }, [field.value]);

    const [fileList, setFileList] = useState<MultiPictureType[]>(defaultFileList);

    const onRemove = useCallback<NonNullable<UploadProps['onRemove']>>(
        file => {
            // manually touch the file
            helpers.setTouched(true);

            // assign file to our known value first
            const castedFile = file as RcFile & { id: string };
            const currentValue = field.value ?? [];
            // delete the file
            helpers.setValue(currentValue.filter(item => item.uid !== castedFile.uid && item.id !== castedFile.id));

            setFileList(prevState =>
                prevState.filter(item => item.uid !== castedFile.uid && item.id !== castedFile.id)
            );
        },
        [field.value, helpers]
    );

    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        async (file, fileList) => {
            if (file.size > sizeLimitInMiB * 1024 * 1024) {
                message.error(t('common:upload.messages.sizeExceedsLimit', { limit: sizeLimitInMiB }));

                return Upload.LIST_IGNORE;
            }

            if (fileList?.length > 0 && file.uid === fileList[fileList.length - 1].uid) {
                const validatingFilesResult = await Promise.all(
                    fileList.map(async (f: RcFile) => {
                        const reader = new FileReader();
                        const promise = new Promise<RcFile>((resolve, reject) => {
                            reader.onload = async event => {
                                const content = event.target.result as string;
                                const validationResult = await validateExtensions(content, f.name);
                                if (validationResult.valid) {
                                    return resolve(f);
                                }

                                return resolve(null);
                            };

                            reader.onerror = event => resolve(null);
                        });
                        reader.readAsBinaryString(f);

                        return Promise.resolve(promise);
                    })
                );
                const validatedFiles = compact(validatingFilesResult);
                if (!validatedFiles.length) {
                    message.error(t('common:upload.messages.fileError'));
                }
                const flist = await Promise.all(
                    validatedFiles.map(async f => {
                        if (f instanceof File) {
                            const result = await readAsDataUrlAsync([f]);
                            const uploadFile: MultiPictureType = {
                                ...f,
                                id: f.uid,
                                uid: f.uid,
                                name: f.name,
                                status: 'done',
                                url: result[0] as string,
                            };

                            return uploadFile;
                        }

                        return f;
                    }) as unknown as MultiPictureType[]
                );

                // TODO: Find a way to assign the type without casting
                const list = validatedFiles as unknown as MultiPictureType[];
                helpers.setValue([...(field.value ?? []), ...list]);

                setFileList(prevState => [...prevState, ...flist]);
            }

            return false;
        },
        [field.value, helpers, sizeLimitInMiB, setFileList, t]
    );

    // DnD handlers
    const [dragEl, setDragEl] = useState<MultiPictureType | undefined>(undefined);
    const setDraggedElement = useCallback(setDragEl, [setDragEl]);

    const onDrop = useCallback(
        (item: MultiPictureType) => {
            const fileListUids = fileList.map(x => x.uid);
            const sorted = fileListUids.map(uid => defaultFileList.find(x => x.uid === uid));
            helpers.setValue([...sorted]);
            helpers.setTouched(true);
        },
        [fileList, defaultFileList, helpers]
    );

    const moveItem = useCallback(
        async (hoveredItem: MultiPictureType) => {
            const prevItems = [...fileList];

            const itemIndex = prevItems.findIndex(i => i.id === dragEl.id);
            const hoverIndex = prevItems.findIndex(i => i.id === hoveredItem.id);

            if (itemIndex !== -1 && hoverIndex !== -1 && itemIndex !== hoverIndex) {
                const newItems = [...fileList];

                newItems.splice(itemIndex, 1);
                newItems.splice(hoverIndex, 0, dragEl);

                setFileList([...newItems]);
            }
        },
        [fileList, setFileList, dragEl]
    );

    return (
        <DropWrapper onDrop={onDrop}>
            <FormItem label={label} meta={meta} name={name} required={required} style={style} tooltip={tooltip}>
                <StyledUpload
                    accept={accept}
                    beforeUpload={beforeUpload}
                    defaultFileList={defaultFileList}
                    disabled={disabled}
                    fileList={fileList}
                    itemRender={(originNode, file) => itemRender(originNode, file, moveItem, setDraggedElement)}
                    listType="picture-card"
                    maxCount={max}
                    onRemove={onRemove}
                    showUploadList={{
                        showPreviewIcon: false,
                    }}
                    multiple
                >
                    <Space>
                        <UploadOutlined />
                        {t('common:upload.button')}
                    </Space>
                </StyledUpload>
            </FormItem>
        </DropWrapper>
    );
};

export default SortableUploadField;
